import { useState } from 'react';
import { Droplets, AlertCircle, Phone, Check, X } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { cn } from '@/lib/utils';
import type { EmergencyEventType } from '../types';

interface EmergencyButtonsProps {
  onReport: (type: EmergencyEventType) => Promise<boolean>;
}

type EventFeedback = {
  type: EmergencyEventType;
  message: string;
  warning?: string;
} | null;

export function EmergencyButtons({ onReport }: EmergencyButtonsProps) {
  const { t } = useTranslation('contractionTimer');
  const [isReporting, setIsReporting] = useState<EmergencyEventType | null>(null);
  const [feedback, setFeedback] = useState<EventFeedback>(null);

  const handleReport = async (type: EmergencyEventType) => {
    if (isReporting) return;

    setIsReporting(type);
    setFeedback(null);

    try {
      const success = await onReport(type);
      if (success) {
        if (type === 'water_break') {
          setFeedback({
            type,
            message: t('emergencyButtons.waterBreakReported'),
            warning: t('emergencyButtons.waterBreakHint'),
          });
        } else if (type === 'bleeding') {
          setFeedback({
            type,
            message: t('emergencyButtons.bleedingReported'),
            warning: t('emergencyButtons.bleedingHint'),
          });
        }
      }
    } finally {
      setIsReporting(null);
    }
  };

  const dismissFeedback = () => {
    setFeedback(null);
  };

  return (
    <div className="space-y-3">
      {/* Feedback Banner */}
      {feedback && (
        <div
          className={cn(
            'rounded-[15px] p-3 flex items-start gap-3',
            feedback.type === 'bleeding' ? 'bg-red-500/20 border border-red-500/30' : 'bg-yellow-500/20 border border-yellow-500/30'
          )}
        >
          <div
            className={cn(
              'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
              feedback.type === 'bleeding' ? 'bg-red-500/30' : 'bg-yellow-500/30'
            )}
          >
            <Check className={cn('w-4 h-4', feedback.type === 'bleeding' ? 'text-red-300' : 'text-yellow-300')} />
          </div>
          <div className="flex-1 min-w-0">
            <p
              className={cn(
                'font-medium text-sm',
                feedback.type === 'bleeding' ? 'text-red-300' : 'text-yellow-300'
              )}
            >
              {feedback.message}
            </p>
            {feedback.warning && (
              <p
                className={cn(
                  'text-xs mt-0.5',
                  feedback.type === 'bleeding' ? 'text-red-400' : 'text-yellow-400'
                )}
              >
                {feedback.warning}
              </p>
            )}
          </div>
          <button
            onClick={dismissFeedback}
            className={cn(
              'p-1 rounded-lg transition-colors flex-shrink-0',
              feedback.type === 'bleeding'
                ? 'hover:bg-red-500/20 text-red-400'
                : 'hover:bg-yellow-500/20 text-yellow-400'
            )}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Emergency Buttons */}
      <div className="grid grid-cols-3 gap-2">
        {/* Water Break Button */}
        <button
          onClick={() => handleReport('water_break')}
          disabled={isReporting !== null}
          className={cn(
            'py-3 px-2 rounded-[15px] font-medium text-sm flex flex-col items-center justify-center gap-1 transition-all min-h-[72px]',
            'bg-yellow-500/30 text-white border border-yellow-500/40 hover:bg-yellow-500/40 active:scale-95',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
        >
          {isReporting === 'water_break' ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <Droplets className="w-5 h-5" />
              <span>{t('emergencyButtons.waterBreak')}</span>
            </>
          )}
        </button>

        {/* Bleeding Button */}
        <button
          onClick={() => handleReport('bleeding')}
          disabled={isReporting !== null}
          className={cn(
            'py-3 px-2 rounded-[15px] font-medium text-sm flex flex-col items-center justify-center gap-1 transition-all min-h-[72px]',
            'bg-red-500/30 text-white border border-red-500/40 hover:bg-red-500/40 active:scale-95',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
        >
          {isReporting === 'bleeding' ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <AlertCircle className="w-5 h-5" />
              <span>{t('emergencyButtons.bleeding')}</span>
            </>
          )}
        </button>

        {/* 112 Emergency Button */}
        <a
          href="tel:112"
          className={cn(
            'py-3 px-2 rounded-[15px] font-bold text-sm flex flex-col items-center justify-center gap-1 transition-all min-h-[72px]',
            'bg-red-600/30 text-white border border-red-500/40 hover:bg-red-600/40 active:scale-95'
          )}
        >
          <Phone className="w-5 h-5" />
          <span>112</span>
        </a>
      </div>
    </div>
  );
}
